import { useSelector } from 'react-redux';
import { useGetKpisQuery } from 'src/context/api/trafficAnalysis/api';
import { fDate } from 'src/utils/format-time';

interface Workspace {
  id?: number;
}

export function useFunnelKpis(
  selectedCountries: string[],
  allCountryNames: string[],
  workspace: Workspace | null
) {
  const filterStore = useSelector((state: any) => state.filters);

  const hasWorkspace = !!workspace?.id;
  const skipMain = !filterStore?.start || !filterStore?.end || !hasWorkspace;
  const skipCompare =
    skipMain || !filterStore?.compare || !filterStore?.compareStart || !filterStore?.compareEnd;

  // The backend filters on `region_name` (country names). An empty array means "all
  // countries" server-side, so a full (or empty) selection is sent as [] and only a
  // strict subset is sent explicitly.
  const countries =
    selectedCountries.length > 0 && selectedCountries.length < allCountryNames.length
      ? selectedCountries
      : [];

  const baseArgs = {
    workspaceId: workspace?.id,
    countries,
    devices: filterStore?.devices ?? [],
    platforms: filterStore?.platforms ?? [],
    keywords: filterStore?.keywords ?? [],
    timestep: filterStore?.timestep,
    tags: (filterStore?.tags ?? []).map((tag: any) => tag.label),
  };

  const main = useGetKpisQuery(
    {
      ...baseArgs,
      startDate: filterStore?.start ? fDate(filterStore.start, 'yyyy-MM-dd') : null,
      endDate: filterStore?.end ? fDate(filterStore.end, 'yyyy-MM-dd') : null,

      compareStartDate: filterStore?.compare && filterStore?.compareStart ? fDate(filterStore.compareStart, 'yyyy-MM-dd') : null,
      compareEndDate: filterStore?.compare && filterStore?.compareEnd ? fDate(filterStore.compareEnd, 'yyyy-MM-dd') : null,
    },
    { skip: skipMain, refetchOnMountOrArgChange: true }
  );

  const compare = useGetKpisQuery(
    {
      ...baseArgs,
      startDate: filterStore?.compareStart ? fDate(filterStore.compareStart, 'yyyy-MM-dd') : null,
      endDate: filterStore?.compareEnd ? fDate(filterStore.compareEnd, 'yyyy-MM-dd') : null,

      compareStartDate: filterStore?.compare && filterStore?.compareStart ? fDate(filterStore.compareStart, 'yyyy-MM-dd') : null,
      compareEndDate: filterStore?.compare && filterStore?.compareEnd ? fDate(filterStore.compareEnd, 'yyyy-MM-dd') : null,
    },
    { skip: skipCompare }
  );

  return {
    mainData: main.data,
    compareData: compare.data,
    error: main.error || compare.error,
    isFetching: main.isFetching || (filterStore?.compare && compare.isFetching),
  };
}
